import React, { useState } from 'react';

// The advisor's "Sponsored owners" page (Succeedo Phase 5, PRD §5-5, §5-6,
// §5-7): their link and the approved invitation, owners who asked for an
// advisor and are waiting, the owners they sponsor (milestones only), the
// briefs owners chose to send (each read is recorded), and their network.

const card = 'rounded-2xl border border-gray-200 bg-white p-5 flex flex-col gap-3';
const h2 = 'm-0 text-[17px] font-semibold text-gray-900';
const muted = 'text-[13px] text-gray-500';
const button = 'rounded-full px-4 py-1.5 text-[13px] font-medium border border-gray-300 text-gray-800 hover:bg-gray-50 disabled:opacity-50';
const primary = 'rounded-full px-4 py-1.5 text-[13px] font-medium bg-gray-900 text-white disabled:opacity-50';
const input = 'rounded-lg border border-gray-300 px-3 py-1.5 text-[13px]';

const SOURCE_LABELS = {
  advisor_link: 'Your link',
  advisor_campaign: 'Campaign',
  enterprise_page: 'Asked for an advisor',
};

const formatDate = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '');

function CopyButton({ text, label = 'Copy' }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (_e) { /* clipboard unavailable */ }
  };
  return <button type="button" className={button} onClick={copy}>{copied ? 'Copied' : label}</button>;
}

function Links({ workspace, onCreateCampaign, busy }) {
  const [campaign, setCampaign] = useState('');
  const kit = workspace.campaign_kit;
  const mailto = `mailto:?subject=${encodeURIComponent(kit.subject)}&body=${encodeURIComponent(kit.body)}`;
  return (
    <section className={card} data-testid="sponsor-links">
      <h2 className={h2}>Invite your business-owner clients</h2>
      <div className="flex flex-wrap items-center gap-2">
        <code className="rounded-lg bg-gray-100 px-3 py-1.5 text-[13px] break-all" data-testid="personal-link">{workspace.personal_link.url}</code>
        <CopyButton text={workspace.personal_link.url} label="Copy link" />
      </div>
      <p className={`m-0 ${muted}`}>
        Owners who join through your link have you on their team in Succeedo. You see that they joined and how far
        they've got; you see what they work on only when they send you a brief.
      </p>
      <div className="flex flex-col gap-2 border-t border-gray-100 pt-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <span className="text-[14px] font-medium text-gray-900">Invitation email</span>
          <span className={`text-[11px] uppercase tracking-wide ${kit.approved ? 'text-green-700' : 'text-gray-500'}`}>
            {kit.approved ? 'Approved by your firm' : 'Standard wording'}
          </span>
        </div>
        <span className="text-[13px] text-gray-900">Subject: {kit.subject}</span>
        <pre className="m-0 whitespace-pre-wrap rounded-lg bg-gray-50 p-3 text-[13px] text-gray-800 font-sans" data-testid="campaign-kit-body">{kit.body}</pre>
        <div className="flex gap-2">
          <CopyButton text={`${kit.subject}\n\n${kit.body}`} label="Copy email" />
          <a className={button} href={mailto}>Open in my email</a>
        </div>
      </div>
      <div className="flex flex-col gap-2 border-t border-gray-100 pt-3">
        <span className="text-[14px] font-medium text-gray-900">Campaign links</span>
        <span className={muted}>A separate link per campaign shows which one brought owners in.</span>
        {workspace.campaign_links.map((l) => (
          <div key={l.id} className="flex flex-wrap items-center gap-2 text-[13px]">
            <span className="font-medium text-gray-900">{l.campaign}</span>
            <code className="rounded bg-gray-100 px-2 py-0.5 break-all">{l.url}</code>
            <CopyButton text={l.url} />
          </div>
        ))}
        <form
          className="flex gap-2"
          onSubmit={(e) => { e.preventDefault(); if (campaign.trim()) onCreateCampaign(campaign.trim()).then(() => setCampaign('')); }}
        >
          <input className={input} placeholder="e.g. Fall client letter" value={campaign} onChange={(e) => setCampaign(e.target.value)} aria-label="Campaign name" />
          <button type="submit" className={button} disabled={busy || !campaign.trim()}>Create link</button>
        </form>
      </div>
    </section>
  );
}

function Waiting({ waiting, onRespond, busy }) {
  if (!waiting.length) return null;
  return (
    <section className={`${card} border-amber-300`} data-testid="sponsor-waiting">
      <h2 className={h2}>Owners who asked for an advisor</h2>
      <p className={`m-0 ${muted}`}>
        They asked to be contacted and you're the match. Claim them to see their name and email; if you pass, the
        next advisor is offered them.
      </p>
      {waiting.map((w) => (
        <div key={w.id} className="flex flex-wrap items-center justify-between gap-3 border-t border-gray-100 pt-3">
          <span className="text-[14px] text-gray-900">
            {w.first_name}{w.state ? `, ${w.state}` : ''} · asked {formatDate(w.asked_at)}
            {w.claim_by && <span className={muted}> · claim by {formatDate(w.claim_by)}</span>}
          </span>
          <span className="flex gap-2">
            <button type="button" className={primary} disabled={busy} onClick={() => onRespond(w.id, true)} data-testid={`claim-${w.id}`}>Claim</button>
            <button type="button" className={button} disabled={busy} onClick={() => onRespond(w.id, false)}>Pass</button>
          </span>
        </div>
      ))}
    </section>
  );
}

function Milestone({ done, label }) {
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] ${done ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
      {done ? '✓ ' : ''}{label}
    </span>
  );
}

function Owners({ owners }) {
  return (
    <section className={card} data-testid="sponsor-owners">
      <h2 className={h2}>Your owners ({owners.length})</h2>
      {owners.length === 0 ? (
        <p className={`m-0 ${muted}`}>No one yet. Share your link or send the invitation above.</p>
      ) : (
        <ul className="m-0 p-0 list-none flex flex-col divide-y divide-gray-100">
          {owners.map((o) => (
            <li key={o.id} className="py-3 flex flex-wrap items-center justify-between gap-3">
              <span className="flex flex-col">
                <span className="text-[14px] font-medium text-gray-900">{o.name}</span>
                <span className={muted}>{o.email} · {SOURCE_LABELS[o.source] || o.source} · joined {formatDate(o.joined_at)}</span>
              </span>
              <span className="flex flex-wrap items-center gap-1.5">
                <Milestone done={o.milestones.assessment} label="Readiness check" />
                <Milestone done={o.milestones.valuation} label="Valuation" />
                <Milestone done={o.milestones.am_i_ok} label="Am I OK?" />
                {o.briefs > 0 && (
                  <span className="rounded-full px-2 py-0.5 text-[11px] bg-amber-50 text-amber-800">
                    {o.briefs} brief{o.briefs > 1 ? 's' : ''}{o.unread_briefs > 0 ? ` · ${o.unread_briefs} new` : ''}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className={`m-0 ${muted}`}>You see milestones, not results or conversations. Owners can remove you at any time.</p>
    </section>
  );
}

function Briefs({ briefs, onRead }) {
  const [open, setOpen] = useState({});
  const [error, setError] = useState('');
  if (!briefs.length) return null;
  const read = async (id) => {
    setError('');
    try {
      const brief = await onRead(id);
      setOpen((o) => ({ ...o, [id]: brief }));
    } catch (e) {
      setError(e?.message || 'Could not open the brief.');
    }
  };
  return (
    <section className={card} data-testid="sponsor-briefs">
      <h2 className={h2}>Briefs from your owners</h2>
      <p className={`m-0 ${muted}`}>Each owner approved their brief word for word. They can see when you've read it.</p>
      {briefs.map((b) => (
        <div key={b.id} className="border-t border-gray-100 pt-3 flex flex-col gap-1.5">
          <span className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-[14px] text-gray-900">
              <strong>{b.owner}</strong> · {b.topic_label} · {formatDate(b.approved_at)}
              {!b.read && !open[b.id] && <span className="ml-2 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] text-amber-800">New</span>}
            </span>
            {!open[b.id] && <button type="button" className={button} onClick={() => read(b.id)} data-testid={`read-brief-${b.id}`}>Read brief</button>}
          </span>
          {open[b.id] && (
            <p className="m-0 whitespace-pre-line rounded-lg bg-gray-50 p-3 text-[14px] text-gray-800" data-testid={`brief-text-${b.id}`}>{open[b.id].brief}</p>
          )}
        </div>
      ))}
      {error && <span className="text-[12px] text-red-700">{error}</span>}
    </section>
  );
}

const EMPTY_CONTACT = { name: '', firm: '', partner_category: 'accountant', email: '', phone: '', note: '' };

function Network({ contacts, categories, onSave, onDelete, busy }) {
  const [form, setForm] = useState(EMPTY_CONTACT);
  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  return (
    <section className={card} data-testid="sponsor-network">
      <h2 className={h2}>Your network</h2>
      <p className={`m-0 ${muted}`}>
        The CPAs, lawyers and other professionals you work with. Your owners see them first when they need that kind
        of help, before the Succeedo marketplace.
      </p>
      {contacts.map((c) => (
        <div key={c.id} className="flex flex-wrap items-center justify-between gap-2 border-t border-gray-100 pt-2 text-[13px]">
          <span><strong>{c.name}</strong>{c.firm ? `, ${c.firm}` : ''} · {c.category_label}{c.email ? ` · ${c.email}` : ''}</span>
          <button type="button" className={button} disabled={busy} onClick={() => onDelete(c.id)}>Remove</button>
        </div>
      ))}
      <form
        className="grid grid-cols-1 md:grid-cols-3 gap-2 border-t border-gray-100 pt-3"
        onSubmit={(e) => { e.preventDefault(); onSave(form).then(() => setForm(EMPTY_CONTACT)); }}
      >
        <input className={input} placeholder="Name" value={form.name} onChange={set('name')} aria-label="Name" required />
        <input className={input} placeholder="Firm" value={form.firm} onChange={set('firm')} aria-label="Firm" />
        <select className={input} value={form.partner_category} onChange={set('partner_category')} aria-label="Kind of professional">
          {categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <input className={input} placeholder="Email" type="email" value={form.email} onChange={set('email')} aria-label="Email" />
        <input className={input} placeholder="Phone" value={form.phone} onChange={set('phone')} aria-label="Phone" />
        <input className={input} placeholder="Note (what they're good at)" value={form.note} onChange={set('note')} aria-label="Note" />
        <button type="submit" className={`${primary} md:col-span-3 justify-self-start`} disabled={busy || !form.name.trim()}>Add to my network</button>
      </form>
    </section>
  );
}

function Profile({ profile, states, onSave, busy }) {
  const [title, setTitle] = useState(profile.title || '');
  const [phone, setPhone] = useState(profile.phone || '');
  const [routed, setRouted] = useState(Boolean(profile.accepts_routed_owners));
  const [stateList, setStateList] = useState((profile.states || []).join(', '));
  const save = () => onSave({
    title, phone, acceptsRoutedOwners: routed,
    states: stateList.split(/[\s,]+/).map((s) => s.trim().toUpperCase()).filter((s) => states.includes(s)),
  });
  return (
    <section className={card} data-testid="sponsor-profile">
      <h2 className={h2}>How owners see you</h2>
      <span className="text-[14px] text-gray-900">{profile.name} · {profile.enterprise}{profile.channel ? ` (${profile.channel})` : ''}</span>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        <input className={input} placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} aria-label="Title" />
        <input className={input} placeholder="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} aria-label="Phone" />
      </div>
      <label className="flex items-start gap-2 text-[14px] text-gray-900">
        <input type="checkbox" checked={routed} onChange={() => setRouted(!routed)} className="mt-1" data-testid="accepts-routed" />
        <span>
          Take owners who ask {profile.enterprise} for an advisor
          <span className={`block ${muted}`}>You'll get an email and have three business days to claim each one.</span>
        </span>
      </label>
      {routed && (
        <input className={input} placeholder="States you cover, e.g. TX, OK (blank for anywhere)" value={stateList} onChange={(e) => setStateList(e.target.value)} aria-label="States" />
      )}
      <button type="button" className={`${primary} self-start`} disabled={busy} onClick={save}>Save</button>
    </section>
  );
}

export default function SponsoredOwnersPage({ workspace, busy, error, onUpdateProfile, onCreateCampaign, onSaveContact, onDeleteContact, onReadBrief, onRespond }) {
  if (!workspace) return null;
  return (
    <div className="mx-auto w-full max-w-4xl px-4 py-8 flex flex-col gap-5" data-testid="sponsored-owners-page">
      <header className="flex flex-col gap-1">
        <h1 className="m-0 text-[28px] font-semibold text-gray-900">Sponsored owners</h1>
        <p className={`m-0 ${muted}`}>
          Business owners you've invited to Succeedo, free for them and offered by {workspace.profile.enterprise}.
        </p>
      </header>
      {error && <p className="m-0 rounded-lg bg-red-50 p-3 text-[13px] text-red-800">{error}</p>}
      <Waiting waiting={workspace.waiting} onRespond={onRespond} busy={busy} />
      <Briefs briefs={workspace.briefs} onRead={onReadBrief} />
      <Owners owners={workspace.owners} />
      <Links workspace={workspace} onCreateCampaign={onCreateCampaign} busy={busy} />
      <Network contacts={workspace.contacts} categories={workspace.categories} onSave={onSaveContact} onDelete={onDeleteContact} busy={busy} />
      <Profile profile={workspace.profile} states={workspace.states} onSave={onUpdateProfile} busy={busy} />
    </div>
  );
}
